import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import moment, { Moment } from 'moment';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { LocationService } from '@app/core/locations/services/location.service';
import { PurchaseOrderStatusInterface } from '@app/interfaces/purchase-order-status.model';
import { trackById } from 'v2/modules/shared/util/track-by.util';

import { PurchaseOrderFacadeService } from './../../purchase-order-facade.service';

@Component({
  selector: 'lum-purchase-order-shipments-filters',
  templateUrl: './purchase-order-shipments-filters.component.html',
  styleUrls: ['./purchase-order-shipments-filters.component.scss'],
})
export class PurchaseOrderShipmentsFiltersComponent implements OnInit {
  form :UntypedFormGroup;
  private onDestroy$ = new Subject();
  warehouses :any[] = [];

  trackById = trackById;

  constructor(
    private purchaseOrderFacadeService :PurchaseOrderFacadeService,
    private snackbar :MatSnackBar,
    private fb :UntypedFormBuilder,
    private locationSvc :LocationService,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.loadShipmentStatus();
    this.loadWarehouses();
  }

  onFilterChange(value :any, property :string) :void {
    const queryParams = {
      ...this.currentQueryParams,
    };

    if (['shippingDateStart', 'shippingDateEnd'].includes(property)) {
      const startDate = (value as Moment).startOf('day').toISOString();
      const endDate = (value as Moment).endOf('day').toISOString();

      // TODO: Create dedicated date range component
      // value is type Moment, so convert the value to date ISO format with 00:00:00 time

      if (property === 'shippingDateStart') {
        // set startDate to iso string with hours, minutes, seconds and milliseconds set to 0
        // check if shipmentDateRange.end is not set, if it is not set, set it to the current value
        if (!this.shipmentDateRangeEndValue) {
          this.form.get('shippingDateEnd').setValue(endDate);
          // set the end date to the current value if it is not set
          queryParams['shippingDateEnd'] =
            queryParams['shippingDateEnd'] || endDate;
        } else if (
          moment(this.shipmentDateRangeEndValue)?.isBefore(moment(startDate))
        ) {
          this.form.get('shippingDateEnd').setValue(startDate);
          queryParams['shippingDateEnd'] = startDate;
        }
        queryParams['shippingDateStart'] = startDate;
      } else {
        // Check if end date is less than the current date, if so set the start date same to end date
        if (this.shipmentDateRangeStartValue?.isAfter(moment(endDate))) {
          this.form.get('shippingDateStart').setValue(endDate);
          queryParams['shippingDateStart'] = endDate;
        }

        queryParams['shippingDateEnd'] = endDate;
      }
    } else {
      queryParams[property] = value;
    }

    this.purchaseOrderFacadeService.setCurrentQueryParams(queryParams);
  }

  onResetFilters() :void {
    // Reset all filters
    this.form.reset();

    const queryParams = {
      ...this.currentQueryParams,
      supplierPurchaseOrderId: '',
      shipmentStatusId: '',
      shippingDateStart: '',
      shippingDateEnd: '',
      warehouseId: '',
    };

    this.purchaseOrderFacadeService.setCurrentQueryParams(queryParams);
  }

  private initForm() :void {
    this.form = this.fb.group({
      supplierPurchaseOrderId: [''],
      shipmentStatusId: [''],
      shippingDateStart: [''],
      shippingDateEnd: [''],
      warehouseId: [''],
    });
  }

  private loadShipmentStatus() :void {
    this.purchaseOrderFacadeService
      .getShipmentStatus()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        error: (error) => {
          this.snackbar.open('Error loading shipment status', 'Fail');
        },
      });
  }

  private loadWarehouses() {
    this.locationSvc
      .getAll({ all: 1, parent_id: 0 })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((resp) => {
        this.warehouses = resp;
      });
  }

  get currentQueryParams() :any {
    return this.purchaseOrderFacadeService.currentShipmentQueryParams;
  }

  get shipmentStatus$() :Observable<PurchaseOrderStatusInterface[]> {
    return this.purchaseOrderFacadeService.shipmentStatus$;
  }

  get shipmentDateRangeStartValue() :any {
    return this.form.get('shippingDateStart')?.value;
  }

  get shipmentDateRangeEndValue() :any {
    return this.form.get('shippingDateEnd')?.value;
  }
}
